
import { Component, Prop, Ref, Vue } from 'nuxt-property-decorator';
import { TranslateResult } from 'vue-i18n';
import { OrganizationDto } from 'fourwaves-shared';
import { Modal } from 'fourwaves-shared/components';
import { isCivicAddressValid } from '~/services/fourwaves';

export type ErrorModalProps = {
  title?: TranslateResult;
  subtitle?: TranslateResult;
  icon?: string;
  actionLess?: boolean;
  actionLabel?: TranslateResult;
  dataTestId?: string;
  action?: () => void;
};

@Component({
  components: {
    Modal,
  },
})
export default class UpgradeCheckoutErrorModal extends Vue {
  @Prop({ type: Object, default: null }) readonly organization!: OrganizationDto | null;
  @Prop(Boolean) readonly isAdministrator!: boolean;
  @Prop(Boolean) readonly isSuperAdmin!: boolean;

  @Ref() readonly modal?: Modal;

  get insufficientPermissionsError(): ErrorModalProps | null {
    if (this.isAdministrator || this.isSuperAdmin) return null;

    return {
      title: this.$t('plans.upgrade_modal.no_admin_title'),
      subtitle: this.$t('plans.upgrade_modal.no_admin_message'),
      icon: 'alert-triangle',
      actionLess: true,
      dataTestId: 'insufficient-permissions',
    };
  }

  get userAccountNotActivatedError(): ErrorModalProps | null {
    if (this.$auth.user?.isEmailConfirmed) return null;

    return {
      title: this.$t('checkout.error_modal.activate_account.title'),
      subtitle: this.$t('checkout.error_modal.activate_account.subtitle', {
        email: this.$auth.user?.email,
      }),
      actionLabel: this.$t('checkout.error_modal.activate_account.actionLabel'),
      icon: 'alert-triangle',
      action: async () => {
        await this.$api.forgotPassword(this.$auth.user?.email);
        this.close();
      },
      dataTestId: 'account-not-activated',
    };
  }

  get missingBillingAddressError(): ErrorModalProps | null {
    const hasValidAddress = this.organization?.civicAddress && isCivicAddressValid(this.organization.civicAddress);

    if (!this.organization || hasValidAddress) return null;
    return {
      title: this.$t('checkout.error_modal.missing_address.title'),
      subtitle: this.$t('checkout.error_modal.missing_address.subtitle'),
      actionLabel: this.$t('checkout.error_modal.missing_address.actionLabel'),
      action: () => {
        this.$router.push(
          this.localePath({
            name: 'organizations-id-settings',
            params: { id: this.organization?.id || '' },
          }),
        );
        this.close();
      },
      icon: 'alert-triangle',
      dataTestId: 'missing-billing-address',
    };
  }

  get errorState() {
    return this.insufficientPermissionsError || this.userAccountNotActivatedError || this.missingBillingAddressError;
  }

  get modalProps() {
    const { action, ...props } = this.errorState || {};
    return props;
  }

  get modalAction() {
    const { action = () => {} } = this.errorState || {};
    return action;
  }

  get hasContextError() {
    return !!this.errorState;
  }

  public open() {
    this.modal?.open();
  }

  public close() {
    this.modal?.close();
  }
}
